import { getEventTypeConfig, isEventFeatureEnabled } from '../config/eventTypes';

export const EVENT_READINESS_FEATURES = [
  {
    id: 'event_details',
    title: 'Event details',
    path: '/dashboard?action=edit-event',
    required: true,
    completeDetail: 'Event identity and dates are set.',
    pendingDetail: 'Add the event name and start date.',
  },
  {
    id: 'itinerary',
    title: 'Itinerary',
    path: '/dashboard/itinerary',
    required: true,
    completeDetail: 'The guest schedule has at least one complete function.',
    pendingDetail: 'Add a function with its date, time and venue.',
    featureKey: 'showItinerary',
  },
  {
    id: 'guests',
    title: 'Guest list',
    path: '/dashboard/guests',
    required: true,
    completeDetail: 'Guests have been added to the event.',
    pendingDetail: 'Add at least one guest to enable guest access.',
  },
  {
    id: 'theme_branding',
    title: 'Guest-view theme',
    path: '/dashboard/theme',
    required: true,
    completeDetail: 'A theme is selected for the guest view.',
    pendingDetail: 'Select a theme for the guest view.',
  },
  {
    id: 'photos',
    title: 'Guest-view hero photos',
    path: '/dashboard/photos',
    required: true,
    completeDetail: 'The guest home has hero or featured media.',
    pendingDetail: 'Add hero media or a featured event photo.',
  },
  {
    id: 'contacts',
    title: 'Key contacts',
    path: '/dashboard/contacts',
    completeDetail: 'At least one contact is available to guests.',
    pendingDetail: 'Add a contact guests can reach during the event.',
  },
  {
    id: 'rooms',
    title: 'Rooms & arrivals',
    path: '/dashboard/rooms',
    completeDetail: 'Accommodation inventory has been added.',
    pendingDetail: 'Set up rooms if the event includes accommodation.',
    featureKey: 'showAccommodation',
  },
  {
    id: 'tasks',
    title: 'Tasks',
    path: '/dashboard/tasks',
    completeDetail: 'Event work is being tracked with tasks.',
    pendingDetail: 'Create tasks if you want to track operational work.',
  },
  {
    id: 'vendors',
    title: 'Vendors',
    path: '/dashboard/vendors',
    completeDetail: 'At least one vendor is assigned to the event.',
    pendingDetail: 'Assign vendors when supplier tracking is needed.',
  },
  {
    id: 'budget',
    title: 'Budget & claims',
    path: '/dashboard/budget',
    completeDetail: 'The event budget is configured.',
    pendingDetail: 'Set up a budget if financial tracking is needed.',
  },
  {
    id: 'wishlist',
    title: "Couple's wishlist",
    path: '/dashboard/gifts',
    featureKey: 'showWishlist',
    completeDetail: 'At least one finalized gift is visible.',
    pendingDetail: 'Add and finalize gifts if the couple wants a wishlist.',
  },
  {
    id: 'team',
    title: 'Team management',
    path: '/dashboard/settings',
    completeDetail: 'At least one team member is assigned.',
    pendingDetail: 'Add team members if others will help manage the event.',
  },
];

const EVENT_READINESS_OVERRIDES = {
  baby_shower: {
    wishlist: {
      title: 'Baby Registry',
      completeDetail: 'At least one finalized registry gift is visible.',
      pendingDetail: 'Add and finalize gifts for the baby and growing family.',
    },
  },
  birthday: {
    wishlist: {
      title: 'Birthday Wishlist',
      completeDetail: 'At least one finalized birthday gift is visible.',
      pendingDetail: 'Add and finalize gift ideas when the celebrant wants a wishlist.',
    },
  },
};

const status = (complete, count) => ({
  complete: Boolean(complete),
  ...(Number.isFinite(count) ? { count } : {}),
});

const hasSelectedTheme = (event = {}) => Boolean(event.theme?.selected_theme);

const hasGuestHeroMedia = (event = {}) => Boolean(
  event.wedding_photos_preview?.length
  || event.guest_experience?.couple_photo_url
  || event.guest_experience?.hero_media?.some((item) => item?.url && item.visible !== false)
);

export function fallbackDashboardReadiness(event = {}, dashboard = {}) {
  const itinerary = event.itinerary || [];
  const guestCount = dashboard.rsvp?.invited ?? dashboard.rsvp?.total ?? 0;
  const hasCompleteItineraryItem = itinerary.some((item) => (
    item?.name?.trim()
    && item.date
    && item.start_time?.trim()
    && (item.venue?.name?.trim() || item.venue?.address?.trim())
  ));
  const required = {
    event_details: status(Boolean(event.name?.trim() && event.start_date)),
  };
  if (isEventFeatureEnabled(event, 'showItinerary')) {
    required.itinerary = status(hasCompleteItineraryItem, itinerary.length);
  }
  Object.assign(required, {
    guests: status(guestCount > 0, guestCount),
    theme_branding: status(hasSelectedTheme(event)),
    photos: status(hasGuestHeroMedia(event)),
  });
  const completedRequired = Object.values(required).filter((item) => item.complete).length;
  const totalRequired = Object.keys(required).length;

  return {
    progress_percent: Math.round((completedRequired / totalRequired) * 100),
    completed_required: completedRequired,
    total_required: totalRequired,
    required,
    features: {
      contacts: status(false),
      rooms: status((dashboard.rooms?.total || 0) > 0, dashboard.rooms?.total || 0),
      tasks: status((dashboard.tasks?.total || 0) > 0, dashboard.tasks?.total || 0),
      vendors: status(false),
      budget: status(Boolean(dashboard.budget?.configured)),
      wishlist: status(false),
      team: status(false),
    },
  };
}

export function getEventReadinessItems(readiness, eventType = 'wedding', event = null) {
  const eventTypeConfig = getEventTypeConfig(eventType);
  const eventContext = event || { event_type: eventType };
  const statuses = {
    ...(readiness?.required || {}),
    ...(readiness?.features || {}),
  };

  return EVENT_READINESS_FEATURES
    .filter((feature) => (
      (!feature.eventTypes || feature.eventTypes.includes(eventType))
      && (!feature.featureKey || (
        ['showItinerary', 'showAccommodation'].includes(feature.featureKey)
          ? isEventFeatureEnabled(eventContext, feature.featureKey)
          : eventTypeConfig.features?.[feature.featureKey]
      ))
    ))
    .map((feature) => {
      const featureStatus = statuses[feature.id] || status(false);
      const configuredFeature = {
        ...feature,
        ...(EVENT_READINESS_OVERRIDES[eventType]?.[feature.id] || {}),
      };
      return {
        ...configuredFeature,
        ...featureStatus,
        detail: featureStatus.complete
          ? configuredFeature.completeDetail
          : configuredFeature.pendingDetail,
      };
    });
}
